import {
  JournalEntry,
  Decision,
  Pattern,
  LifeContext,
  Person,
  Place,
  Language
} from '../types';
import { getPrompts } from '../constants/aiPrompts';

// ============================================
// SEMANTIC SEARCH WITH EMBEDDINGS
// Uses backend API endpoints for security
// ============================================

const getEmbedding = async (text: string): Promise<number[]> => {
  try {
    const response = await fetch('/api/ai/embedding', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ text: text.substring(0, 500) }),
    });
    if (!response.ok) {
      console.error('Embedding API error:', response.status);
      return [];
    }
    const data = await response.json();
    return data.embedding || [];
  } catch (error) {
    console.error('Embedding error:', error);
    return [];
  }
};

const cosineSimilarity = (a: number[], b: number[]): number => {
  if (a.length !== b.length || a.length === 0) return 0;
  let dotProduct = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB) || 1);
};

export interface SemanticMatch {
  entry: JournalEntry;
  similarity: number;
  isHighlyRelevant: boolean;
}

export const getSemanticallySimilarEntries = async (
  query: string,
  entries: JournalEntry[],
  maxEntries: number = 10,
  similarityThreshold: number = 0.3
): Promise<SemanticMatch[]> => {
  if (entries.length === 0 || !query.trim()) return [];
  
  try {
    const queryEmbedding = await getEmbedding(query);
    if (queryEmbedding.length === 0) return [];
    
    // IMPORTANT: Process ALL entries to find old but relevant memories
    // If there are too many entries, we batch them to avoid API limits
    const MAX_BATCH_SIZE = 100;
    const allMatches: SemanticMatch[] = [];
    
    // Process entries in batches to cover the entire history
    for (let batchStart = 0; batchStart < entries.length; batchStart += MAX_BATCH_SIZE) {
      const batch = entries.slice(batchStart, batchStart + MAX_BATCH_SIZE);
      const entryTexts = batch.map(e => e.content.substring(0, 400));
      
      try {
        const response = await fetch('/api/ai/embeddings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ texts: entryTexts }),
        });
        
        if (!response.ok) {
          console.error('Batch embedding API error:', response.status);
          continue;
        }
        
        const data = await response.json();
        const embeddings = data.embeddings || [];
        
        for (let i = 0; i < batch.length; i++) {
          const entryEmbedding = embeddings[i] || [];
          if (entryEmbedding.length === 0) continue;
          
          const similarity = cosineSimilarity(queryEmbedding, entryEmbedding);
          if (similarity >= similarityThreshold) {
            allMatches.push({
              entry: batch[i],
              similarity,
              isHighlyRelevant: similarity >= 0.5
            });
          }
        }
      } catch (batchError) {
        console.error(`Semantic batch error at ${batchStart}:`, batchError);
        // Continue with other batches even if one fails
      }
      
      // Limit total batches to avoid excessive API calls (max 500 entries)
      if (batchStart >= 400) break;
    }
    
    return allMatches
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, maxEntries);
  } catch (error) {
    console.error('Semantic search error:', error);
    return [];
  }
};

// ============================================
// WEIGHTED ENTRY SCORING SYSTEM
// (Rebalanced: less recency weight, more semantic relevance)
// ============================================

interface ScoredEntry {
  entry: JournalEntry;
  score: number;
  relevanceFactors: {
    recency: number;
    emotionalImpact: number;
    goalRelevance: number;
    connectionStrength: number;
    keywordMatch: number;
    semanticSimilarity: number;
  };
}

const DAY_MS = 1000 * 60 * 60 * 24;

export const scoreEntry = (
  entry: JournalEntry,
  query: string,
  context: LifeContext,
  allEntries: JournalEntry[],
  semanticSimilarity: number = 0
): ScoredEntry => {
  const now = Date.now();
  const daysSince = (now - entry.timestamp) / DAY_MS;
  
  // REBALANCED SCORING SYSTEM
  // Old: recency 30, emotional 25, goal 20, connection 15, keyword 10 = 100
  // New: semantic 35, emotional 25, goal 20, connection 15, recency 10, keyword 10 = 115 (normalized)
  
  // 1. SEMANTIC SIMILARITY (0-35 points) - THE MOST IMPORTANT FACTOR
  // This ensures old but relevant entries surface
  const semanticScore = semanticSimilarity * 35;
  
  // 2. RECENCY SCORE (0-10 points) - REDUCED from 30
  // Still matters for context freshness, but shouldn't bury relevant old memories
  const recencyScore = Math.max(0, 10 * Math.exp(-daysSince / 60)); // Slower decay (60 days instead of 30)
  
  // 3. EMOTIONAL IMPACT SCORE (0-25 points)
  const emotionalIntensity = Math.abs(entry.goalAlignment - 50) / 50;
  const hasStrongEmotion = ['😢', '😡', '🎉', '😍', '🤯', '💪', '😰', '😤', '🥺', '💔', '🙏'].includes(entry.emotion);
  const emotionalScore = emotionalIntensity * 15 + (hasStrongEmotion ? 10 : 0);
  
  // 4. GOAL/VALUE RELEVANCE (0-20 points)
  const queryLower = query.toLowerCase();
  const contentLower = entry.content.toLowerCase();
  let goalRelevance = 0;
  
  for (const goal of context.goals) {
    if (contentLower.includes(goal.toLowerCase().substring(0, 10))) {
      goalRelevance += 5;
    }
    if (queryLower.includes(goal.toLowerCase().substring(0, 10))) {
      goalRelevance += 5;
    }
  }
  
  for (const value of context.values) {
    if (contentLower.includes(value.toLowerCase().substring(0, 8))) {
      goalRelevance += 3;
    }
  }
  goalRelevance = Math.min(20, goalRelevance);
  
  // 5. SEMANTIC CONNECTION STRENGTH (0-15 points)
  let connectionScore = 0;
  if (entry.relatedTo && entry.relatedTo.length > 0) {
    connectionScore = Math.min(15, entry.relatedTo.length * 3 + 
      entry.relatedTo.reduce((sum, link) => sum + link.strength / 20, 0));
  }
  
  // 6. KEYWORD & CATEGORY MATCH (0-10 points)
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 3);
  let keywordMatch = 0;
  for (const word of queryWords) {
    if (contentLower.includes(word)) keywordMatch += 2;
    if (entry.category.toLowerCase().includes(word)) keywordMatch += 3;
  }
  keywordMatch = Math.min(10, keywordMatch);
  
  // 7. PEOPLE/ENTITY MATCH BONUS (0-10 points)
  // If query mentions a person that appears in this entry, boost it
  let entityBonus = 0;
  if (entry.people && entry.people.length > 0) {
    for (const person of entry.people) {
      if (queryLower.includes(person.name.toLowerCase())) {
        entityBonus += 5;
      }
    }
  }
  entityBonus = Math.min(10, entityBonus);
  
  const totalScore = semanticScore + recencyScore + emotionalScore + goalRelevance + connectionScore + keywordMatch + entityBonus;
  
  return {
    entry,
    score: totalScore,
    relevanceFactors: {
      recency: recencyScore,
      emotionalImpact: emotionalScore,
      goalRelevance,
      connectionStrength: connectionScore,
      keywordMatch,
      semanticSimilarity: semanticScore
    }
  };
};

export const getTopScoredEntries = (
  entries: JournalEntry[],
  query: string,
  context: LifeContext,
  maxEntries: number = 20
): ScoredEntry[] => {
  const scored = entries.map(e => scoreEntry(e, query, context, entries, 0));
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, maxEntries);
};

export const getTopScoredEntriesWithSemantics = async (
  entries: JournalEntry[],
  query: string,
  context: LifeContext,
  maxEntries: number = 20
): Promise<ScoredEntry[]> => {
  if (entries.length === 0) return [];
  
  const semanticMatches = await getSemanticallySimilarEntries(query, entries, 50, 0.2);
  
  const similarityMap = new Map<string, number>();
  for (const match of semanticMatches) {
    similarityMap.set(match.entry.id, match.similarity);
  }
  
  const scored = entries.map(e => {
    const similarity = similarityMap.get(e.id) || 0;
    return scoreEntry(e, query, context, entries, similarity);
  });
  
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, maxEntries);
};

// ============================================
// PEOPLE CONTEXT AGGREGATION
// ============================================

export interface PersonContext {
  name: string;
  normalizedName: string;
  relationship: string;
  frequency: number;
  avgMoodImpact: number;
  lastMentioned: number;
  categories: string[];
  emotionalTone: 'positive' | 'negative' | 'mixed' | 'neutral';
  recentMoments: string[];
  isLovedOne: boolean; // True for wife, husband, partner, child, etc.
  sentimentContext: 'beloved' | 'supportive' | 'complex' | 'neutral' | 'challenging'; // More nuanced than emotionalTone
}

// Protected relationships that should never be interpreted as "negative" without strong evidence
const LOVED_ONE_RELATIONSHIPS = [
  'moglie', 'wife', 'esposa', 'ehefrau', 'femme',
  'marito', 'husband', 'marido', 'ehemann', 'mari',
  'partner', 'compagno', 'compagna', 'pareja', 'partenaire',
  'figlio', 'figlia', 'son', 'daughter', 'hijo', 'hija', 'sohn', 'tochter', 'fils', 'fille',
  'madre', 'mother', 'mom', 'mamma', 'mamá', 'mutter', 'mère',
  'padre', 'father', 'dad', 'papà', 'papá', 'vater', 'père',
  'fidanzato', 'fidanzata', 'boyfriend', 'girlfriend', 'novio', 'novia', 'freund', 'freundin', 'copain', 'copine',
  'fratello', 'sorella', 'brother', 'sister', 'hermano', 'hermana', 'bruder', 'schwester', 'frère', 'sœur'
];

// Check if relationship indicates a loved one
const isLovedOneRelationship = (relationship: string): boolean => {
  const normalized = relationship.toLowerCase().trim();
  return LOVED_ONE_RELATIONSHIPS.some(r => normalized.includes(r));
};

// Analyze sentiment from actual content - checks if worry/stress is FOR someone vs CAUSED BY someone
const analyzeContentSentiment = (moments: string[]): 'beloved' | 'supportive' | 'complex' | 'neutral' | 'challenging' => {
  if (moments.length === 0) return 'neutral';

  const allContent = moments.join(' ').toLowerCase();

  // Positive/caring indicators (worry FOR, love, miss, support)
  const lovingPatterns = [
    /(?:amo|love|ama|liebe|aime)/i,
    /(?:mi manc|miss|me falta|vermisse|manque)/i,
    /(?:preoccupat[oa] per|worried about|preocupad[oa] por|besorgt um|inquiet pour)/i,
    /(?:orgoglios[oa] di|proud of|orgullos[oa] de|stolz auf|fier de)/i,
    /(?:felice con|happy with|feliz con|glücklich mit|heureux avec)/i,
    /(?:insieme a|together with|junto[as]? con|zusammen mit|ensemble avec)/i,
    /(?:supporto|support|apoyo|unterstützung|soutien)/i,
    /(?:abbracci|hugs|abrazo|umarmung|câlin)/i,
    /(?:baci|kisses|besos|küsse|bisous)/i,
    /(?:tesoro|darling|cariño|schatz|chéri)/i,
    /(?:amore mio|my love|mi amor|meine liebe|mon amour)/i
  ];

  // Conflict indicators (fight, anger AT, problem with)
  const conflictPatterns = [
    /(?:litigat[oa]|fought|peleado|gestritten|disputé)/i,
    /(?:arrabbiat[oa] con|angry at|enojad[oa] con|wütend auf|fâché contre)/i,
    /(?:problema con|problem with|problema con|problem mit|problème avec)/i,
    /(?:stufo di|fed up with|harto de|satt von|marre de)/i,
    /(?:delusio|disappoint|decepcion|enttäusch|déçu)/i
  ];

  let lovingScore = 0;
  let conflictScore = 0;

  for (const pattern of lovingPatterns) {
    if (pattern.test(allContent)) lovingScore++;
  }

  for (const pattern of conflictPatterns) {
    if (pattern.test(allContent)) conflictScore++;
  }

  if (lovingScore >= 3) return 'beloved';
  if (lovingScore >= 1 && conflictScore === 0) return 'supportive';
  if (conflictScore >= 2 && lovingScore === 0) return 'challenging';
  if (lovingScore > 0 && conflictScore > 0) return 'complex';
  return 'neutral';
};

export const getPeopleContext = (
  entries: JournalEntry[],
  maxPeople: number = 10
): PersonContext[] => {
  const peopleMap = new Map<string, {
    names: string[];
    relationship: string;
    moodImpacts: number[];
    lastMentioned: number;
    categories: string[];
    moments: Array<{ id: string; timestamp: number; content: string }>;
  }>();
  
  for (const entry of entries) {
    if (!entry.people || entry.people.length === 0) continue;
    
    for (const person of entry.people) {
      const normalized = person.name.toLowerCase().trim();
      
      // Skip generic terms
      if (['io', 'me', 'lui', 'lei', 'noi', 'loro', 'i', 'me', 'he', 'she', 'we', 'they'].includes(normalized)) {
        continue;
      }
      
      const existing = peopleMap.get(normalized);
      if (existing) {
        existing.names.push(person.name);
        if (person.impactOnMood !== undefined) {
          existing.moodImpacts.push(person.impactOnMood);
        }
        existing.lastMentioned = Math.max(existing.lastMentioned, entry.timestamp);
        if (!existing.categories.includes(entry.category)) {
          existing.categories.push(entry.category);
        }
        existing.moments.push({ id: entry.id, timestamp: entry.timestamp, content: entry.content.substring(0, 100) });
        if (person.relationship && !existing.relationship) {
          existing.relationship = person.relationship;
        }
      } else {
        peopleMap.set(normalized, {
          names: [person.name],
          relationship: person.relationship || 'unknown',
          moodImpacts: person.impactOnMood !== undefined ? [person.impactOnMood] : [],
          lastMentioned: entry.timestamp,
          categories: [entry.category],
          moments: [{ id: entry.id, timestamp: entry.timestamp, content: entry.content.substring(0, 100) }]
        });
      }
    }
  }
  
  // Convert to PersonContext array with scoring
  const peopleContexts: PersonContext[] = [];
  
  for (const [normalized, data] of peopleMap) {
    const frequency = data.moments.length;
    const avgMoodImpact = data.moodImpacts.length > 0
      ? data.moodImpacts.reduce((a, b) => a + b, 0) / data.moodImpacts.length
      : 0;
    
    // Get most frequent name form
    const nameCounts = data.names.reduce((acc, name) => {
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const displayName = Object.entries(nameCounts).sort((a, b) => b[1] - a[1])[0][0];

    // Recent moments (last 5 for better sentiment analysis)
    const recentMoments = data.moments
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5)
      .map(m => m.content);

    // Check if this is a loved one based on relationship
    const isLovedOne = isLovedOneRelationship(data.relationship);

    // Analyze actual content sentiment (not just mood impact)
    const sentimentContext = analyzeContentSentiment(recentMoments);

    // IMPROVED: Determine emotional tone with relationship awareness
    // For loved ones, negative mood impacts often mean worry FOR them, not conflict WITH them
    let emotionalTone: 'positive' | 'negative' | 'mixed' | 'neutral' = 'neutral';
    if (data.moodImpacts.length > 0) {
      const positive = data.moodImpacts.filter(m => m > 20).length;
      const negative = data.moodImpacts.filter(m => m < -20).length;

      if (isLovedOne) {
        // For loved ones, use content sentiment as primary signal
        if (sentimentContext === 'beloved' || sentimentContext === 'supportive') {
          emotionalTone = 'positive';
        } else if (sentimentContext === 'challenging') {
          emotionalTone = 'mixed'; // Never fully negative for loved ones without strong evidence
        } else if (sentimentContext === 'complex') {
          emotionalTone = 'mixed';
        } else {
          // Fallback to mood impact but with higher threshold for negative
          if (positive > negative) emotionalTone = 'positive';
          else if (negative > positive * 3) emotionalTone = 'mixed'; // Much higher threshold for loved ones
          else if (positive > 0 || negative > 0) emotionalTone = 'mixed';
        }
      } else {
        // Standard logic for non-loved-ones
        if (positive > negative * 2) emotionalTone = 'positive';
        else if (negative > positive * 2) emotionalTone = 'negative';
        else if (positive > 0 && negative > 0) emotionalTone = 'mixed';
      }
    }

    peopleContexts.push({
      name: displayName,
      normalizedName: normalized,
      relationship: data.relationship,
      frequency,
      avgMoodImpact,
      lastMentioned: data.lastMentioned,
      categories: data.categories,
      emotionalTone,
      recentMoments,
      isLovedOne,
      sentimentContext
    });
  }
  
  // Score and sort by importance
  return peopleContexts
    .sort((a, b) => {
      // Weighting: frequency * 2 + recency bonus + emotional significance
      const recencyA = (Date.now() - a.lastMentioned) < DAY_MS * 7 ? 5 : 0;
      const recencyB = (Date.now() - b.lastMentioned) < DAY_MS * 7 ? 5 : 0;
      const emotionA = Math.abs(a.avgMoodImpact) > 30 ? 3 : 0;
      const emotionB = Math.abs(b.avgMoodImpact) > 30 ? 3 : 0;
      return (b.frequency * 2 + recencyB + emotionB) - (a.frequency * 2 + recencyA + emotionA);
    })
    .slice(0, maxPeople);
};

// ============================================
// PLACES CONTEXT AGGREGATION
// ============================================

export interface PlaceContext {
  name: string;
  type: string;
  frequency: number;
  avgGoalAlignment: number;
  emotionalAssociation: 'positive' | 'negative' | 'neutral';
  dominantEmotions: string[];
  categories: string[];
  lastVisited: number;
  contextDescription: string; // More nuanced description for AI context
}

// Place types that indicate daily life (home, work city) vs vacation/special places
const HOME_PLACE_TYPES = ['city', 'città', 'home', 'casa', 'house', 'apartment', 'office', 'ufficio', 'work', 'lavoro'];

// Generate a nuanced context description for a place
const getPlaceContextDescription = (
  type: string,
  avgAlignment: number,
  frequency: number,
  categories: string[]
): string => {
  const isHomePlace = HOME_PLACE_TYPES.some(t => type.toLowerCase().includes(t));
  const isWorkPlace = categories.includes('Career') || categories.includes('Lavoro') || categories.includes('Work');

  if (isHomePlace || frequency > 20) {
    // High-frequency places are where daily life happens - stress here is normal, not the place's fault
    if (avgAlignment < 40) {
      return 'daily life base (stress reflects life challenges, not the place itself)';
    } else if (avgAlignment < 60) {
      return 'daily life base (mixed experiences, typical)';
    } else {
      return 'daily life base (positive associations)';
    }
  } else if (isWorkPlace) {
    if (avgAlignment < 40) {
      return 'work location (work stress, not place-specific)';
    } else {
      return 'work location (productive space)';
    }
  } else {
    // Less frequent places - emotional association is more meaningful
    if (avgAlignment < 35) {
      return 'challenging experiences here';
    } else if (avgAlignment > 65) {
      return 'positive experiences here';
    } else {
      return 'neutral/varied experiences';
    }
  }
};

export const getPlacesContext = (
  entries: JournalEntry[],
  maxPlaces: number = 8
): PlaceContext[] => {
  const placesMap = new Map<string, {
    names: string[];
    type: string;
    alignments: number[];
    emotions: string[];
    categories: string[];
    lastVisited: number;
  }>();
  
  for (const entry of entries) {
    if (!entry.places || entry.places.length === 0) continue;
    
    for (const place of entry.places) {
      const normalized = place.name.toLowerCase().trim();
      
      const existing = placesMap.get(normalized);
      if (existing) {
        existing.alignments.push(entry.goalAlignment);
        existing.emotions.push(entry.emotion);
        existing.lastVisited = Math.max(existing.lastVisited, entry.timestamp);
        if (!existing.categories.includes(entry.category)) {
          existing.categories.push(entry.category);
        }
      } else {
        placesMap.set(normalized, {
          names: [place.name],
          type: place.type || 'unknown',
          alignments: [entry.goalAlignment],
          emotions: [entry.emotion],
          categories: [entry.category],
          lastVisited: entry.timestamp
        });
      }
    }
  }
  
  const placesContexts: PlaceContext[] = [];
  
  for (const [normalized, data] of placesMap) {
    const frequency = data.alignments.length;
    const avgAlignment = data.alignments.reduce((a, b) => a + b, 0) / data.alignments.length;
    
    // Determine emotional association
    let emotionalAssociation: 'positive' | 'negative' | 'neutral' = 'neutral';
    if (avgAlignment > 65) emotionalAssociation = 'positive';
    else if (avgAlignment < 35) emotionalAssociation = 'negative';
    
    // Get dominant emotions
    const emotionCounts = data.emotions.reduce((acc, e) => {
      acc[e] = (acc[e] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const dominantEmotions = Object.entries(emotionCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([e]) => e);
    
    // Get most frequent name variant (canonical display name)
    const nameCounts = data.names.reduce((acc, name) => {
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const displayName = Object.entries(nameCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || normalized;
    
    // Dedupe categories
    const uniqueCategories = [...new Set(data.categories)];

    // Generate nuanced context description
    const contextDescription = getPlaceContextDescription(
      data.type,
      avgAlignment,
      frequency,
      uniqueCategories
    );

    placesContexts.push({
      name: displayName,
      type: data.type,
      frequency,
      avgGoalAlignment: Math.round(avgAlignment),
      emotionalAssociation,
      dominantEmotions,
      categories: uniqueCategories,
      lastVisited: data.lastVisited,
      contextDescription
    });
  }
  
  return placesContexts
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, maxPlaces);
};

// ============================================
// TEMPORAL PATTERNS ANALYSIS
// ============================================

export interface TemporalPattern {
  type: 'time_of_day' | 'day_of_week' | 'monthly';
  pattern: string;
  description: string;
  avgAlignment: number;
  frequency: number;
  dominantCategories: string[];
  dominantEmotions: string[];
}

export const getTemporalPatterns = (
  entries: JournalEntry[],
  language: Language = 'English'
): TemporalPattern[] => {
  if (entries.length < 3) return [];
  
  const patterns: TemporalPattern[] = [];
  
  // Time of Day Analysis
  const timeOfDayStats: Record<string, { count: number; alignments: number[]; categories: string[]; emotions: string[] }> = {
    morning: { count: 0, alignments: [], categories: [], emotions: [] },
    afternoon: { count: 0, alignments: [], categories: [], emotions: [] },
    evening: { count: 0, alignments: [], categories: [], emotions: [] },
    night: { count: 0, alignments: [], categories: [], emotions: [] }
  };
  
  // Day of Week Analysis
  const dayOfWeekStats: Record<number, { count: number; alignments: number[]; categories: string[]; emotions: string[] }> = {};
  for (let i = 0; i < 7; i++) {
    dayOfWeekStats[i] = { count: 0, alignments: [], categories: [], emotions: [] };
  }
  
  for (const entry of entries) {
    const date = new Date(entry.timestamp);
    const hour = date.getHours();
    const dayOfWeek = date.getDay();
    
    // Determine time of day
    let timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night' = 'afternoon';
    if (hour >= 5 && hour < 12) timeOfDay = 'morning';
    else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
    else if (hour >= 17 && hour < 21) timeOfDay = 'evening';
    else timeOfDay = 'night';
    
    timeOfDayStats[timeOfDay].count++;
    timeOfDayStats[timeOfDay].alignments.push(entry.goalAlignment);
    timeOfDayStats[timeOfDay].categories.push(entry.category);
    timeOfDayStats[timeOfDay].emotions.push(entry.emotion);
    
    dayOfWeekStats[dayOfWeek].count++;
    dayOfWeekStats[dayOfWeek].alignments.push(entry.goalAlignment);
    dayOfWeekStats[dayOfWeek].categories.push(entry.category);
    dayOfWeekStats[dayOfWeek].emotions.push(entry.emotion);
  }
  
  // Generate time of day patterns
  const timeLabels: Record<string, Record<Language, string>> = {
    morning: { Italian: 'mattina', English: 'morning', Spanish: 'mañana', German: 'Morgen', French: 'matin' },
    afternoon: { Italian: 'pomeriggio', English: 'afternoon', Spanish: 'tarde', German: 'Nachmittag', French: 'après-midi' },
    evening: { Italian: 'sera', English: 'evening', Spanish: 'noche', German: 'Abend', French: 'soir' },
    night: { Italian: 'notte', English: 'night', Spanish: 'noche', German: 'Nacht', French: 'nuit' }
  };
  
  for (const [time, stats] of Object.entries(timeOfDayStats)) {
    if (stats.count < 3) continue;
    
    const avgAlignment = stats.alignments.reduce((a, b) => a + b, 0) / stats.alignments.length;
    const categoryCounts = stats.categories.reduce((acc, c) => { acc[c] = (acc[c] || 0) + 1; return acc; }, {} as Record<string, number>);
    const emotionCounts = stats.emotions.reduce((acc, e) => { acc[e] = (acc[e] || 0) + 1; return acc; }, {} as Record<string, number>);
    
    patterns.push({
      type: 'time_of_day',
      pattern: time,
      description: timeLabels[time][language] || time,
      avgAlignment: Math.round(avgAlignment),
      frequency: stats.count,
      dominantCategories: Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([c]) => c),
      dominantEmotions: Object.entries(emotionCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([e]) => e)
    });
  }
  
  // Generate day of week patterns
  const dayLabels: Record<number, Record<Language, string>> = {
    0: { Italian: 'Domenica', English: 'Sunday', Spanish: 'Domingo', German: 'Sonntag', French: 'Dimanche' },
    1: { Italian: 'Lunedì', English: 'Monday', Spanish: 'Lunes', German: 'Montag', French: 'Lundi' },
    2: { Italian: 'Martedì', English: 'Tuesday', Spanish: 'Martes', German: 'Dienstag', French: 'Mardi' },
    3: { Italian: 'Mercoledì', English: 'Wednesday', Spanish: 'Miércoles', German: 'Mittwoch', French: 'Mercredi' },
    4: { Italian: 'Giovedì', English: 'Thursday', Spanish: 'Jueves', German: 'Donnerstag', French: 'Jeudi' },
    5: { Italian: 'Venerdì', English: 'Friday', Spanish: 'Viernes', German: 'Freitag', French: 'Vendredi' },
    6: { Italian: 'Sabato', English: 'Saturday', Spanish: 'Sábado', German: 'Samstag', French: 'Samedi' }
  };
  
  for (const [day, stats] of Object.entries(dayOfWeekStats)) {
    if (stats.count < 3) continue;
    
    const dayNum = parseInt(day);
    const avgAlignment = stats.alignments.reduce((a, b) => a + b, 0) / stats.alignments.length;
    const categoryCounts = stats.categories.reduce((acc, c) => { acc[c] = (acc[c] || 0) + 1; return acc; }, {} as Record<string, number>);
    const emotionCounts = stats.emotions.reduce((acc, e) => { acc[e] = (acc[e] || 0) + 1; return acc; }, {} as Record<string, number>);
    
    patterns.push({
      type: 'day_of_week',
      pattern: day,
      description: dayLabels[dayNum][language] || `Day ${day}`,
      avgAlignment: Math.round(avgAlignment),
      frequency: stats.count,
      dominantCategories: Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([c]) => c),
      dominantEmotions: Object.entries(emotionCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([e]) => e)
    });
  }
  
  return patterns;
};

// ============================================
// EMOTIONAL PULSE - Deep emotional intelligence
// ============================================

export interface EmotionalPulse {
  distribution7d: Record<string, number>;
  distribution30d: Record<string, number>;
  dominantEmotion: string;
  emotionalIntensity: 'low' | 'medium' | 'high' | 'extreme';
  trendNarrative: string;
  recentShift: 'improving' | 'declining' | 'stable' | 'volatile';
  streakType: 'positive' | 'negative' | 'mixed' | null;
  streakDays: number;
}

const emotionValence: Record<string, number> = {
  '😊': 70, '😄': 80, '🎉': 90, '😍': 85, '💪': 75, '✨': 70, '🙏': 65, '😌': 60,
  '😐': 50, '🤔': 50, '😶': 45,
  '😢': 25, '😰': 30, '😡': 20, '😔': 35, '🥺': 30, '😩': 25, '😤': 30, '🤯': 40,
  '😴': 45, '🙂': 55, '😅': 50
};

const getEmotionValence = (emoji: string): number => emotionValence[emoji] ?? 50;

const getTimestampMs = (timestamp: string | number): number => {
  if (typeof timestamp === 'number') return timestamp;
  return new Date(timestamp).getTime();
};

export const getEmotionalPulse = (
  entries: JournalEntry[],
  language: Language = 'English'
): EmotionalPulse => {
  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;
  
  const entries7d = entries.filter(e => (now - getTimestampMs(e.timestamp)) < 7 * DAY_MS);
  const entries30d = entries.filter(e => (now - getTimestampMs(e.timestamp)) < 30 * DAY_MS);
  
  const countEmotions = (arr: JournalEntry[]): Record<string, number> => {
    return arr.reduce((acc, e) => {
      acc[e.emotion] = (acc[e.emotion] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  };
  
  const distribution7d = countEmotions(entries7d);
  const distribution30d = countEmotions(entries30d);
  
  const dominantEmotion = Object.entries(distribution7d)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || '😊';
  
  const recentValences = entries.slice(0, 10).map(e => getEmotionValence(e.emotion));
  const avgValence = recentValences.length > 0 
    ? recentValences.reduce((a, b) => a + b, 0) / recentValences.length 
    : 50;
  
  const valenceVariance = recentValences.length > 0
    ? recentValences.reduce((sum, v) => sum + Math.pow(v - avgValence, 2), 0) / recentValences.length
    : 0;
  
  let emotionalIntensity: 'low' | 'medium' | 'high' | 'extreme' = 'medium';
  if (valenceVariance > 400) emotionalIntensity = 'extreme';
  else if (valenceVariance > 200) emotionalIntensity = 'high';
  else if (valenceVariance < 50) emotionalIntensity = 'low';
  
  const first5Valence = recentValences.slice(0, 5).reduce((a, b) => a + b, 0) / Math.max(1, recentValences.slice(0, 5).length);
  const last5Valence = recentValences.slice(5, 10).reduce((a, b) => a + b, 0) / Math.max(1, recentValences.slice(5, 10).length) || first5Valence;
  
  let recentShift: 'improving' | 'declining' | 'stable' | 'volatile' = 'stable';
  if (valenceVariance > 300) recentShift = 'volatile';
  else if (first5Valence > last5Valence + 15) recentShift = 'improving';
  else if (first5Valence < last5Valence - 15) recentShift = 'declining';
  
  let streakType: 'positive' | 'negative' | 'mixed' | null = null;
  let streakDays = 0;
  if (entries.length >= 3) {
    const recentThree = entries.slice(0, 3).map(e => getEmotionValence(e.emotion));
    if (recentThree.every(v => v >= 60)) { streakType = 'positive'; streakDays = 3; }
    else if (recentThree.every(v => v <= 40)) { streakType = 'negative'; streakDays = 3; }
    else { streakType = 'mixed'; }
  }
  
  const narratives: Record<Language, Record<string, string>> = {
    Italian: {
      improving_high: 'Settimana intensa ma in netto miglioramento',
      improving_medium: 'Stai ritrovando equilibrio dopo giorni difficili',
      declining_high: 'Periodo emotivamente turbolento, tendenza negativa',
      declining_medium: 'Qualche difficoltà in più negli ultimi giorni',
      stable_positive: 'Periodo sereno e stabile',
      stable_negative: 'Persistente stato di difficoltà',
      stable_neutral: 'Settimana nella media',
      volatile: 'Emozioni molto altalenanti, su e giù frequenti',
      no_data: 'Dati insufficienti per analisi'
    },
    English: {
      improving_high: 'Intense week but clearly improving',
      improving_medium: 'Finding balance after tough days',
      declining_high: 'Emotionally turbulent period, negative trend',
      declining_medium: 'Some extra challenges lately',
      stable_positive: 'Calm and stable period',
      stable_negative: 'Persistent difficult state',
      stable_neutral: 'Average week',
      volatile: 'Highly fluctuating emotions',
      no_data: 'Not enough data for analysis'
    },
    Spanish: {
      improving_high: 'Semana intensa pero mejorando claramente',
      improving_medium: 'Encontrando equilibrio después de días difíciles',
      declining_high: 'Período emocionalmente turbulento',
      declining_medium: 'Algunos desafíos extra últimamente',
      stable_positive: 'Período tranquilo y estable',
      stable_negative: 'Estado difícil persistente',
      stable_neutral: 'Semana promedio',
      volatile: 'Emociones muy fluctuantes',
      no_data: 'Datos insuficientes'
    },
    German: {
      improving_high: 'Intensive Woche, aber deutliche Verbesserung',
      improving_medium: 'Balance nach schwierigen Tagen finden',
      declining_high: 'Emotional turbulente Zeit',
      declining_medium: 'Einige zusätzliche Herausforderungen',
      stable_positive: 'Ruhige und stabile Zeit',
      stable_negative: 'Anhaltend schwieriger Zustand',
      stable_neutral: 'Durchschnittliche Woche',
      volatile: 'Stark schwankende Emotionen',
      no_data: 'Nicht genug Daten'
    },
    French: {
      improving_high: 'Semaine intense mais en nette amélioration',
      improving_medium: 'Retrouver l\'équilibre après des jours difficiles',
      declining_high: 'Période émotionnellement turbulente',
      declining_medium: 'Quelques défis supplémentaires',
      stable_positive: 'Période calme et stable',
      stable_negative: 'État difficile persistant',
      stable_neutral: 'Semaine moyenne',
      volatile: 'Émotions très fluctuantes',
      no_data: 'Données insuffisantes'
    }
  };
  
  const lang = narratives[language] || narratives.English;
  let narrativeKey = 'stable_neutral';
  
  if (entries7d.length < 3) narrativeKey = 'no_data';
  else if (recentShift === 'volatile') narrativeKey = 'volatile';
  else if (recentShift === 'improving' && emotionalIntensity === 'high') narrativeKey = 'improving_high';
  else if (recentShift === 'improving') narrativeKey = 'improving_medium';
  else if (recentShift === 'declining' && emotionalIntensity === 'high') narrativeKey = 'declining_high';
  else if (recentShift === 'declining') narrativeKey = 'declining_medium';
  else if (avgValence >= 60) narrativeKey = 'stable_positive';
  else if (avgValence <= 40) narrativeKey = 'stable_negative';
  
  return {
    distribution7d,
    distribution30d,
    dominantEmotion,
    emotionalIntensity,
    trendNarrative: lang[narrativeKey],
    recentShift,
    streakType,
    streakDays
  };
};

// ============================================
// LIFE DIMENSIONS TREND
// ============================================

export interface DimensionsTrend {
  salute: { total: number; trend: 'up' | 'down' | 'stable'; count: number };
  focus: { total: number; trend: 'up' | 'down' | 'stable'; count: number };
  connessione: { total: number; trend: 'up' | 'down' | 'stable'; count: number };
  spirito: { total: number; trend: 'up' | 'down' | 'stable'; count: number };
  finanza: { total: number; trend: 'up' | 'down' | 'stable'; count: number };
  strongestArea: string;
  weakestArea: string;
  summary: string;
}

export const getDimensionsTrend = (
  entries: JournalEntry[],
  language: Language = 'English'
): DimensionsTrend => {
  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;
  
  const entries14d = entries.filter(e => (now - getTimestampMs(e.timestamp)) < 14 * DAY_MS && e.dimensionImpacts);
  const entries7dOld = entries.filter(e => (now - getTimestampMs(e.timestamp)) >= 7 * DAY_MS && (now - getTimestampMs(e.timestamp)) < 14 * DAY_MS && e.dimensionImpacts);
  const entries7dRecent = entries.filter(e => (now - getTimestampMs(e.timestamp)) < 7 * DAY_MS && e.dimensionImpacts);
  
  const sumDimension = (arr: JournalEntry[], dim: keyof NonNullable<JournalEntry['dimensionImpacts']>): number => {
    return arr.reduce((sum, e) => sum + (e.dimensionImpacts?.[dim] || 0), 0);
  };
  
  const calcTrend = (recent: number, old: number): 'up' | 'down' | 'stable' => {
    if (recent > old + 3) return 'up';
    if (recent < old - 3) return 'down';
    return 'stable';
  };
  
  const dims: Array<keyof NonNullable<JournalEntry['dimensionImpacts']>> = ['salute', 'focus', 'connessione', 'spirito', 'finanza'];
  const result: Record<string, { total: number; trend: 'up' | 'down' | 'stable'; count: number }> = {};
  
  for (const dim of dims) {
    const total = sumDimension(entries14d, dim);
    const recentSum = sumDimension(entries7dRecent, dim);
    const oldSum = sumDimension(entries7dOld, dim);
    result[dim] = {
      total,
      trend: calcTrend(recentSum, oldSum),
      count: entries14d.filter(e => (e.dimensionImpacts?.[dim] || 0) !== 0).length
    };
  }
  
  const sorted = dims.map(d => ({ dim: d, total: result[d].total })).sort((a, b) => b.total - a.total);
  const strongestArea = sorted[0]?.dim || 'focus';
  const weakestArea = sorted[sorted.length - 1]?.dim || 'finanza';
  
  const areaNames: Record<Language, Record<string, string>> = {
    Italian: { salute: 'Salute', focus: 'Focus', connessione: 'Connessione', spirito: 'Spirito', finanza: 'Finanza' },
    English: { salute: 'Health', focus: 'Focus', connessione: 'Connection', spirito: 'Spirit', finanza: 'Finance' },
    Spanish: { salute: 'Salud', focus: 'Enfoque', connessione: 'Conexión', spirito: 'Espíritu', finanza: 'Finanzas' },
    German: { salute: 'Gesundheit', focus: 'Fokus', connessione: 'Verbindung', spirito: 'Geist', finanza: 'Finanzen' },
    French: { salute: 'Santé', focus: 'Focus', connessione: 'Connexion', spirito: 'Esprit', finanza: 'Finance' }
  };
  
  const names = areaNames[language] || areaNames.English;
  const summary = `${names[strongestArea]} (+${result[strongestArea].total}) | ${names[weakestArea]} (${result[weakestArea].total})`;
  
  return {
    salute: result.salute,
    focus: result.focus,
    connessione: result.connessione,
    spirito: result.spirito,
    finanza: result.finanza,
    strongestArea,
    weakestArea,
    summary
  };
};

// ============================================
// EMOTIONAL TRIGGERS ANALYSIS
// ============================================

export interface EmotionalTrigger {
  trigger: string;
  frequency: number;
  avgMoodImpact: number;
  associatedEmotions: string[];
  isPositive: boolean;
}

export const getFrequentTriggers = (
  entries: JournalEntry[],
  maxTriggers: number = 6
): EmotionalTrigger[] => {
  const triggerMap = new Map<string, {
    count: number;
    moodImpacts: number[];
    emotions: string[];
  }>();
  
  for (const entry of entries.slice(0, 50)) {
    const triggers = entry.emotionalTriggers || [];
    const moodImpact = getEmotionValence(entry.emotion) - 50;
    
    for (const trigger of triggers) {
      const normalized = trigger.toLowerCase().trim();
      if (normalized.length < 3) continue;
      
      const existing = triggerMap.get(normalized);
      if (existing) {
        existing.count++;
        existing.moodImpacts.push(moodImpact);
        existing.emotions.push(entry.emotion);
      } else {
        triggerMap.set(normalized, {
          count: 1,
          moodImpacts: [moodImpact],
          emotions: [entry.emotion]
        });
      }
    }
  }
  
  const results: EmotionalTrigger[] = [];
  
  for (const [trigger, data] of triggerMap) {
    if (data.count < 2) continue;
    
    const avgMoodImpact = data.moodImpacts.reduce((a, b) => a + b, 0) / data.moodImpacts.length;
    const emotionCounts = data.emotions.reduce((acc, e) => {
      acc[e] = (acc[e] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const associatedEmotions = Object.entries(emotionCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([e]) => e);
    
    results.push({
      trigger,
      frequency: data.count,
      avgMoodImpact: Math.round(avgMoodImpact),
      associatedEmotions,
      isPositive: avgMoodImpact > 10
    });
  }
  
  return results
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, maxTriggers);
};

// ============================================
// CATEGORY HEALTH ANALYSIS
// ============================================

export interface CategoryHealth {
  category: string;
  avgAlignment: number;
  entryCount: number;
  trend: 'improving' | 'declining' | 'stable';
  status: 'thriving' | 'healthy' | 'needs_attention' | 'critical';
  recentEmotions: string[];
}

export const getCategoryHealth = (
  entries: JournalEntry[],
  language: Language = 'English'
): CategoryHealth[] => {
  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;
  
  const categoryMap = new Map<string, {
    alignments: Array<{ value: number; timestamp: number }>;
    emotions: string[];
  }>();
  
  for (const entry of entries.slice(0, 60)) {
    const cat = entry.category;
    const ts = getTimestampMs(entry.timestamp);
    const existing = categoryMap.get(cat);
    if (existing) {
      existing.alignments.push({ value: entry.goalAlignment, timestamp: ts });
      existing.emotions.push(entry.emotion);
    } else {
      categoryMap.set(cat, {
        alignments: [{ value: entry.goalAlignment, timestamp: ts }],
        emotions: [entry.emotion]
      });
    }
  }
  
  const results: CategoryHealth[] = [];
  
  for (const [category, data] of categoryMap) {
    const avgAlignment = data.alignments.reduce((sum, a) => sum + a.value, 0) / data.alignments.length;
    
    const recent = data.alignments.filter(a => (now - a.timestamp) < 7 * DAY_MS);
    const older = data.alignments.filter(a => (now - a.timestamp) >= 7 * DAY_MS && (now - a.timestamp) < 14 * DAY_MS);
    
    const recentAvg = recent.length > 0 ? recent.reduce((s, a) => s + a.value, 0) / recent.length : avgAlignment;
    const olderAvg = older.length > 0 ? older.reduce((s, a) => s + a.value, 0) / older.length : recentAvg;
    
    let trend: 'improving' | 'declining' | 'stable' = 'stable';
    if (recentAvg > olderAvg + 10) trend = 'improving';
    else if (recentAvg < olderAvg - 10) trend = 'declining';
    
    let status: 'thriving' | 'healthy' | 'needs_attention' | 'critical' = 'healthy';
    if (avgAlignment >= 70) status = 'thriving';
    else if (avgAlignment >= 50) status = 'healthy';
    else if (avgAlignment >= 35) status = 'needs_attention';
    else status = 'critical';
    
    const emotionCounts = data.emotions.reduce((acc, e) => {
      acc[e] = (acc[e] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const recentEmotions = Object.entries(emotionCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([e]) => e);
    
    results.push({
      category,
      avgAlignment: Math.round(avgAlignment),
      entryCount: data.alignments.length,
      trend,
      status,
      recentEmotions
    });
  }
  
  return results.sort((a, b) => b.entryCount - a.entryCount);
};

// ============================================
// DECISION LEARNING - SIMILAR PAST OUTCOMES
// ============================================

export interface DecisionLearning {
  similarDecisions: Array<{
    id: string;
    title: string;
    similarity: number;
    outcome: 'positive' | 'negative' | 'neutral' | 'mixed';
    chosenOption: string;
    lesson: string;
  }>;
  successRate: number;
  commonPatterns: string[];
}

export const getDecisionLearning = (
  currentDecisionContext: string,
  decisions: Decision[],
  maxSimilar: number = 5
): DecisionLearning => {
  const resolvedDecisions = decisions.filter(d => d.status === 'resolved' && d.outcome);
  
  if (resolvedDecisions.length === 0) {
    return { similarDecisions: [], successRate: 0, commonPatterns: [] };
  }
  
  const currentWords = new Set(
    currentDecisionContext.toLowerCase().split(/\s+/).filter(w => w.length > 3)
  );
  
  // Calculate similarity scores
  const scoredDecisions = resolvedDecisions.map(d => {
    const decisionWords = new Set(
      `${d.title} ${d.context}`.toLowerCase().split(/\s+/).filter(w => w.length > 3)
    );
    
    const intersection = [...currentWords].filter(w => decisionWords.has(w)).length;
    const union = new Set([...currentWords, ...decisionWords]).size;
    const similarity = union > 0 ? (intersection / union) * 100 : 0;
    
    return { decision: d, similarity };
  });
  
  const topSimilar = scoredDecisions
    .filter(s => s.similarity > 15)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, maxSimilar);
  
  const similarDecisions = topSimilar.map(s => ({
    id: s.decision.id,
    title: s.decision.title,
    similarity: Math.round(s.similarity),
    outcome: s.decision.outcome!.outcome,
    chosenOption: s.decision.outcome!.chosenOption,
    lesson: s.decision.outcome!.lessonsLearned?.[0] || ''
  }));
  
  // Calculate success rate
  const positiveCount = resolvedDecisions.filter(d => d.outcome?.outcome === 'positive').length;
  const successRate = Math.round((positiveCount / resolvedDecisions.length) * 100);
  
  // Extract common patterns from positive outcomes
  const positiveDecisions = resolvedDecisions.filter(d => d.outcome?.outcome === 'positive');
  const allLessons = positiveDecisions.flatMap(d => d.outcome?.lessonsLearned || []);
  const commonPatterns = [...new Set(allLessons)].slice(0, 3);
  
  return { similarDecisions, successRate, commonPatterns };
};

// ============================================
// EXTRACT PLACES (AI-POWERED)
// ============================================

export const extractPlaces = async (
  content: string,
  imageBase64?: string,
  language: Language = 'English'
): Promise<Place[]> => {
  const prompts = getPrompts(language);
  
  const systemPrompt = `You are a location extraction assistant. Extract all places mentioned in the text.

RESPONSE FORMAT (JSON):
{
  "places": [
    { "name": "place name", "type": "home|office|gym|restaurant|cafe|outdoor|travel|other", "emotionalAssociation": "positive|negative|neutral" }
  ]
}

RULES:
- Extract specific location names (e.g., "Milano", "Central Park", "home office")
- Infer type from context
- Infer emotional association from surrounding context
- If no places mentioned, return {"places": []}
- Maximum 5 places per content`;

  try {
    const userMessage = imageBase64
      ? {
          role: 'user',
          content: [
            { type: 'text', text: content },
            { type: 'image_url', image_url: { url: imageBase64, detail: 'low' } }
          ]
        }
      : { role: 'user', content };

    const response = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        messages: [
          { role: 'system', content: systemPrompt },
          userMessage
        ],
        options: { jsonMode: true, maxTokens: 256, hasImage: !!imageBase64 }
      }),
    });

    if (!response.ok) {
      console.error('Place extraction API error:', response.status);
      return [];
    }

    const data = await response.json();
    const result = JSON.parse(data.content || '{"places":[]}');
    return result.places || [];
  } catch (error) {
    console.error('Place extraction error:', error);
    return [];
  }
};

// ============================================
// ENHANCED CONTEXT SUMMARY FOR CHAT
// ============================================

export interface EnhancedContext {
  // Core context
  summary: string;
  
  // Weighted entries
  topEntries: ScoredEntry[];
  
  // Social graph
  keyPeople: PersonContext[];
  
  // Location graph
  keyPlaces: PlaceContext[];
  
  // Temporal intelligence
  temporalPatterns: TemporalPattern[];
  bestTimeOfDay: string | null;
  bestDayOfWeek: string | null;
  
  // Decision intelligence
  decisionLearning: DecisionLearning | null;
  
  // Aggregated insights
  currentMood: string;
  dominantCategory: string;
  alignmentTrend: 'improving' | 'declining' | 'stable';
  
  // DEEP CONTEXT - New emotional intelligence
  emotionalPulse: EmotionalPulse;
  dimensionsTrend: DimensionsTrend;
  frequentTriggers: EmotionalTrigger[];
  categoryHealth: CategoryHealth[];
}

export const buildEnhancedContext = (
  query: string,
  context: LifeContext,
  entries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[]
): EnhancedContext => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const lang = context.language || 'English';
  
  // Get weighted entries (sync version - no semantic search)
  const topEntries = getTopScoredEntries(safeEntries, query, context, 15);
  
  return buildEnhancedContextInternal(query, context, safeEntries, decisions, patterns, topEntries, lang);
};

export const buildEnhancedContextWithSemantics = async (
  query: string,
  context: LifeContext,
  entries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[],
  conversationHistory: Array<{ role: string; content: string }> = []
): Promise<EnhancedContext> => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const lang = context.language || 'English';
  
  // DYNAMIC CONTEXT: Build enriched search query from current message + recent conversation
  // This allows finding relevant memories that weren't considered at conversation start
  let enrichedQuery = query;
  
  if (conversationHistory.length > 0) {
    // Extract key topics from last 3 messages for context enrichment
    const recentMessages = conversationHistory.slice(-3);
    const conversationContext = recentMessages
      .map(m => m.content.substring(0, 100))
      .join(' ');
    
    // Combine current query with conversation context for broader semantic search
    // Weight: current query (full) + recent context (abbreviated)
    enrichedQuery = `${query} ${conversationContext}`;
  }
  
  // Get weighted entries WITH semantic search using enriched query
  // This finds memories relevant to the entire conversation, not just current message
  const topEntries = await getTopScoredEntriesWithSemantics(safeEntries, enrichedQuery, context, 15);
  
  return buildEnhancedContextInternal(query, context, safeEntries, decisions, patterns, topEntries, lang);
};

const buildEnhancedContextInternal = (
  query: string,
  context: LifeContext,
  safeEntries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[],
  topEntries: ScoredEntry[],
  lang: Language
): EnhancedContext => {
  
  // Get people context
  const keyPeople = getPeopleContext(safeEntries, 8);
  
  // Get places context
  const keyPlaces = getPlacesContext(safeEntries, 6);
  
  // Get temporal patterns
  const temporalPatterns = getTemporalPatterns(safeEntries, lang);
  
  // Find best time of day (highest avg alignment)
  const timePatterns = temporalPatterns.filter(p => p.type === 'time_of_day');
  const bestTimePattern = timePatterns.sort((a, b) => b.avgAlignment - a.avgAlignment)[0];
  const bestTimeOfDay = bestTimePattern?.description || getLocalizedFallback('not_enough_data', lang);
  
  // Find best day of week
  const dayPatterns = temporalPatterns.filter(p => p.type === 'day_of_week');
  const bestDayPattern = dayPatterns.sort((a, b) => b.avgAlignment - a.avgAlignment)[0];
  const bestDayOfWeek = bestDayPattern?.description || getLocalizedFallback('not_enough_data', lang);
  
  // Decision learning (if query relates to decision)
  const isDecisionQuery = query.toLowerCase().match(/(decid|scelt|choice|option|should|dovrei|potrei|forse|entscheidung|décision)/);
  const decisionLearning = isDecisionQuery ? getDecisionLearning(query, decisions) : null;
  
  // Current mood from recent entries
  const recentEmotions = safeEntries.slice(0, 5).map(e => e.emotion);
  const currentMood = recentEmotions[0] || '😊';
  
  // Dominant category
  const categoryCounts = safeEntries.slice(0, 20).reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const dominantCategory = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || 'Focus';
  
  // Alignment trend
  const recentAlignments = safeEntries.slice(0, 10).map(e => e.goalAlignment);
  const olderAlignments = safeEntries.slice(10, 20).map(e => e.goalAlignment);
  const recentAvg = recentAlignments.length > 0 
    ? recentAlignments.reduce((a, b) => a + b, 0) / recentAlignments.length 
    : 50;
  const olderAvg = olderAlignments.length > 0 
    ? olderAlignments.reduce((a, b) => a + b, 0) / olderAlignments.length 
    : recentAvg;
  const alignmentTrend: 'improving' | 'declining' | 'stable' = 
    recentAvg > olderAvg + 10 ? 'improving' : 
    recentAvg < olderAvg - 10 ? 'declining' : 'stable';
  
  // DEEP CONTEXT - New emotional intelligence
  const emotionalPulse = getEmotionalPulse(safeEntries, lang);
  const dimensionsTrend = getDimensionsTrend(safeEntries, lang);
  const frequentTriggers = getFrequentTriggers(safeEntries, 6);
  const categoryHealth = getCategoryHealth(safeEntries, lang);
  
  // Build summary string
  const summary = buildContextSummaryString(
    context, topEntries, keyPeople, keyPlaces, 
    temporalPatterns, patterns, decisionLearning
  );
  
  return {
    summary,
    topEntries,
    keyPeople,
    keyPlaces,
    temporalPatterns,
    bestTimeOfDay,
    bestDayOfWeek,
    decisionLearning,
    currentMood,
    dominantCategory,
    alignmentTrend,
    emotionalPulse,
    dimensionsTrend,
    frequentTriggers,
    categoryHealth
  };
};

// Localized fallback strings for enhanced context
const getLocalizedFallback = (key: string, language: Language): string => {
  const fallbacks: Record<string, Record<Language, string>> = {
    not_enough_data: {
      Italian: 'dati insufficienti',
      English: 'not enough data',
      Spanish: 'datos insuficientes',
      German: 'nicht genug Daten',
      French: 'données insuffisantes'
    },
    no_similar_decisions: {
      Italian: 'nessuna decisione simile trovata',
      English: 'no similar decisions found',
      Spanish: 'no se encontraron decisiones similares',
      German: 'keine ähnlichen Entscheidungen gefunden',
      French: 'aucune décision similaire trouvée'
    }
  };
  return fallbacks[key]?.[language] || fallbacks[key]?.['English'] || key;
};

// Localized section headers for context summary
interface LocalizedSectionHeaders {
  peopleInYourLife: string;
  significantPlaces: string;
  temporalPatterns: string;
  similarPastDecisions: string;
  mentions: string;
  moodImpact: string;
  alignment: string;
  bestTime: string;
  challengingTime: string;
  avgAlignment: string;
  successRate: string;
  lesson: string;
}

const getLocalizedSectionHeaders = (language: Language): LocalizedSectionHeaders => {
  const headers: Record<Language, LocalizedSectionHeaders> = {
    Italian: {
      peopleInYourLife: 'PERSONE NELLA TUA VITA',
      significantPlaces: 'LUOGHI SIGNIFICATIVI',
      temporalPatterns: 'PATTERN TEMPORALI',
      similarPastDecisions: 'DECISIONI PASSATE SIMILI',
      mentions: 'menzioni',
      moodImpact: 'impatto umore',
      alignment: 'allineamento',
      bestTime: 'Momento migliore',
      challengingTime: 'Momento difficile',
      avgAlignment: 'allineamento medio',
      successRate: 'tasso di successo',
      lesson: 'Lezione'
    },
    English: {
      peopleInYourLife: 'PEOPLE IN YOUR LIFE',
      significantPlaces: 'SIGNIFICANT PLACES',
      temporalPatterns: 'TEMPORAL PATTERNS',
      similarPastDecisions: 'SIMILAR PAST DECISIONS',
      mentions: 'mentions',
      moodImpact: 'mood impact',
      alignment: 'alignment',
      bestTime: 'Best time',
      challengingTime: 'Challenging time',
      avgAlignment: 'avg alignment',
      successRate: 'success rate',
      lesson: 'Lesson'
    },
    Spanish: {
      peopleInYourLife: 'PERSONAS EN TU VIDA',
      significantPlaces: 'LUGARES SIGNIFICATIVOS',
      temporalPatterns: 'PATRONES TEMPORALES',
      similarPastDecisions: 'DECISIONES PASADAS SIMILARES',
      mentions: 'menciones',
      moodImpact: 'impacto en ánimo',
      alignment: 'alineación',
      bestTime: 'Mejor momento',
      challengingTime: 'Momento difícil',
      avgAlignment: 'alineación promedio',
      successRate: 'tasa de éxito',
      lesson: 'Lección'
    },
    German: {
      peopleInYourLife: 'MENSCHEN IN DEINEM LEBEN',
      significantPlaces: 'BEDEUTENDE ORTE',
      temporalPatterns: 'ZEITLICHE MUSTER',
      similarPastDecisions: 'ÄHNLICHE VERGANGENE ENTSCHEIDUNGEN',
      mentions: 'Erwähnungen',
      moodImpact: 'Stimmungseinfluss',
      alignment: 'Ausrichtung',
      bestTime: 'Beste Zeit',
      challengingTime: 'Herausfordernde Zeit',
      avgAlignment: 'durchschn. Ausrichtung',
      successRate: 'Erfolgsrate',
      lesson: 'Lektion'
    },
    French: {
      peopleInYourLife: 'PERSONNES DANS TA VIE',
      significantPlaces: 'LIEUX SIGNIFICATIFS',
      temporalPatterns: 'MODÈLES TEMPORELS',
      similarPastDecisions: 'DÉCISIONS PASSÉES SIMILAIRES',
      mentions: 'mentions',
      moodImpact: 'impact humeur',
      alignment: 'alignement',
      bestTime: 'Meilleur moment',
      challengingTime: 'Moment difficile',
      avgAlignment: 'alignement moyen',
      successRate: 'taux de réussite',
      lesson: 'Leçon'
    }
  };
  return headers[language] || headers['English'];
};

const buildContextSummaryString = (
  context: LifeContext,
  topEntries: ScoredEntry[],
  keyPeople: PersonContext[],
  keyPlaces: PlaceContext[],
  temporalPatterns: TemporalPattern[],
  patterns: Pattern[],
  decisionLearning: DecisionLearning | null
): string => {
  const prompts = getPrompts(context.language);
  const cl = prompts.contextLabels;
  
  const sections: string[] = [];
  
  // Identity & Core Values
  sections.push(`${cl.userIdentity}: ${context.userName || cl.user}

${cl.coreValuesSystem}:
${context.values.map(v => `• ${v}`).join('\n') || `• ${cl.inDefinition}`}

${cl.goalsAspirations}:
${context.goals.map(g => `• ${g}`).join('\n') || `• ${cl.inDefinition}`}

${cl.currentObstacles}:
${context.blockers?.map(b => `• ${b}`).join('\n') || `• ${cl.noBlockersIdentified}`}`);

  // Localized section headers
  const sectionHeaders = getLocalizedSectionHeaders(context.language);
  
  // Key People (Social Graph) - with enhanced relationship context
  if (keyPeople.length > 0) {
    const peopleSection = keyPeople.slice(0, 5).map(p => {
      // Use sentiment context for loved ones, emotional tone for others
      const sentimentLabel = p.isLovedOne ? p.sentimentContext : p.emotionalTone;
      const relationshipNote = p.isLovedOne ? '❤️' : '';
      const moodIndicator = p.emotionalTone === 'positive' ? '💚' :
                           p.emotionalTone === 'negative' ? '💔' :
                           p.emotionalTone === 'mixed' ? '💛' : '⚪';
      return `• ${p.name} (${p.relationship}) ${relationshipNote}${moodIndicator} - ${p.frequency}x ${sectionHeaders.mentions}, sentiment: ${sentimentLabel}`;
    }).join('\n');
    sections.push(`\n🧑‍🤝‍🧑 ${sectionHeaders.peopleInYourLife}:\n${peopleSection}`);
  }
  
  // Key Places (Location Graph) - with nuanced context
  if (keyPlaces.length > 0) {
    const placesSection = keyPlaces.slice(0, 4).map(p => {
      const toneIndicator = p.emotionalAssociation === 'positive' ? '✨' :
                           p.emotionalAssociation === 'negative' ? '😔' : '➖';
      return `• ${p.name} (${p.type}) ${toneIndicator} - ${p.frequency}x, context: ${p.contextDescription}`;
    }).join('\n');
    sections.push(`\n📍 ${sectionHeaders.significantPlaces}:\n${placesSection}`);
  }
  
  // Temporal Intelligence
  const bestTimePattern = temporalPatterns
    .filter(p => p.type === 'time_of_day')
    .sort((a, b) => b.avgAlignment - a.avgAlignment)[0];
  const worstTimePattern = temporalPatterns
    .filter(p => p.type === 'time_of_day')
    .sort((a, b) => a.avgAlignment - b.avgAlignment)[0];
  
  if (bestTimePattern && worstTimePattern && bestTimePattern.pattern !== worstTimePattern.pattern) {
    sections.push(`\n⏰ ${sectionHeaders.temporalPatterns}:
• ${sectionHeaders.bestTime}: ${bestTimePattern.description} (${sectionHeaders.avgAlignment}: ${bestTimePattern.avgAlignment}%)
• ${sectionHeaders.challengingTime}: ${worstTimePattern.description} (${sectionHeaders.avgAlignment}: ${worstTimePattern.avgAlignment}%)`);
  }
  
  // Patterns
  if (patterns.length > 0) {
    const patternSection = patterns.slice(0, 5).map(p =>
      `• ${p.isPositive ? '✅' : '⚠️'} ${p.title} (${p.frequency}x) - ${p.insight}`
    ).join('\n');
    sections.push(`\n${cl.patternsIdentifiedBehavior}:\n${patternSection}`);
  }
  
  // Decision Learning
  if (decisionLearning && decisionLearning.similarDecisions.length > 0) {
    const decisionSection = decisionLearning.similarDecisions.slice(0, 3).map(d => {
      const outcomeIcon = d.outcome === 'positive' ? '✅' : 
                         d.outcome === 'negative' ? '❌' : '➖';
      return `• ${outcomeIcon} "${d.title}" → ${d.chosenOption} (${d.outcome})${d.lesson ? ` - ${sectionHeaders.lesson}: ${d.lesson}` : ''}`;
    }).join('\n');
    sections.push(`\n📊 ${sectionHeaders.similarPastDecisions} (${sectionHeaders.successRate}: ${decisionLearning.successRate}%):\n${decisionSection}`);
  }
  
  // Recent History (weighted)
  if (topEntries.length > 0) {
    const entriesSection = topEntries.slice(0, 10).map(se => {
      const e = se.entry;
      const scoreInfo = `[score: ${Math.round(se.score)}]`;
      return `• ${new Date(e.timestamp).toLocaleDateString()}: ${e.content.substring(0, 80)}... (${e.category}, ${e.emotion}, alignment: ${e.goalAlignment}%) ${scoreInfo}`;
    }).join('\n');
    sections.push(`\n${cl.recentHistory} (weighted by relevance):\n${entriesSection}`);
  }
  
  sections.push(`\n${cl.criticalNote}`);
  
  return sections.join('\n');
};

// ============================================
// UTILITY: ADD TIME METADATA TO ENTRY
// ============================================

export const enrichEntryWithTimeMetadata = (entry: JournalEntry): JournalEntry => {
  const date = new Date(entry.timestamp);
  const hour = date.getHours();
  
  let timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night' = 'afternoon';
  if (hour >= 5 && hour < 12) timeOfDay = 'morning';
  else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
  else if (hour >= 17 && hour < 21) timeOfDay = 'evening';
  else timeOfDay = 'night';
  
  return {
    ...entry,
    timeOfDay,
    dayOfWeek: date.getDay()
  };
};
